import get from 'lodash/get';
import type * as Types from './types';

export const Result = (item?: any): Types.IEntity.Result => {
  const proctoringEventsCount =
    typeof get(item, 'proctoring_events_count') === 'number'
      ? get(item, 'proctoring_events_count')
      : typeof get(item, 'tab_switches') === 'number'
      ? get(item, 'tab_switches')
      : undefined;

  return {
    id: get(item, 'id') || '',
    testId: get(item, 'test_id') || '',
    testTitle: get(item, 'test_title') || '',
    userId: get(item, 'user_id') || '',
    userName: get(item, 'user_name') || '',
    phone: get(item, 'phone') || '',
    email: get(item, 'email') || '',
    score: get(item, 'score') || 0,
    maxScore: get(item, 'max_score') || 0,
    percentage: get(item, 'percentage') || 0,
    passed: get(item, 'passed') ?? null,
    subjectScores: get(item, 'subject_scores') || undefined,
    tabSwitches: proctoringEventsCount,
    proctoringEventsCount,
    status: get(item, 'status') || 'in_progress',
    startedAt: get(item, 'started_at') ? new Date(get(item, 'started_at')) : new Date(),
    completedAt: get(item, 'completed_at') ? new Date(get(item, 'completed_at')) : null,
    duration: get(item, 'duration') || 0,
    answersCount: (get(item, 'answers') || []).length,
    examScore: get(item, 'exam_score') || null,
    examSkills: get(item, 'exam_skills') || null,
  };
};

/** The saved texts by language; anything that is not a string is dropped rather than trusted. */
const analysisTexts = (raw: unknown): Record<string, string> =>
  raw && typeof raw === 'object'
    ? Object.fromEntries(Object.entries(raw as Record<string, unknown>).filter(([, v]) => typeof v === 'string')) as Record<string, string>
    : {};

/** `ai_analysis_subs` of a reading passage: one offer per sub-question id, or nothing when there is none. */
const subAnalysisOffers = (raw: unknown) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const entries = Object.entries(raw as Record<string, any>)
    .filter(([, offer]) => offer?.available === true)
    .map(([id, offer]) => [
      id,
      { available: true as const, texts: analysisTexts(offer.texts), ...(offer.locked === true ? { locked: true as const } : {}) },
    ] as const);
  return entries.length ? { aiSubAnalysis: Object.fromEntries(entries) } : null;
};

const analysisOf = (raw: any): NonNullable<Types.IEntity.ResultDetails['analysis']> => ({
  ...(Array.isArray(raw.question_types)
    ? {
        questionTypes: raw.question_types
          .filter((r: any) => typeof r?.type === 'string' && typeof r?.correct === 'number' && typeof r?.total === 'number')
          .map((r: any) => ({ type: r.type, correct: r.correct, total: r.total })),
      }
    : {}),
  ...(raw.question_types_locked === true ? { questionTypesLocked: true as const } : {}),
  ...(Array.isArray(raw.section_timing)
    ? {
        sectionTiming: raw.section_timing
          .filter((r: any) => typeof r?.used_seconds === 'number')
          .map((r: any) => ({
            sectionId: String(r.section_id ?? ''),
            sectionTitle: String(r.section_title ?? ''),
            usedSeconds: r.used_seconds,
            limitSeconds: typeof r.limit_seconds === 'number' ? r.limit_seconds : null,
          })),
      }
    : {}),
  ...(raw.section_timing_locked === true ? { sectionTimingLocked: true as const } : {}),
});

export const ResultDetails = (item?: any): Types.IEntity.ResultDetails => ({
  ...Result(item),
  timeLimit: typeof get(item, 'time_limit') === 'number' ? get(item, 'time_limit') : null,
  scaledReport: get(item, 'scaled_report') || null,
  correctAnswersHidden: get(item, 'correct_answers_hidden') === true,
  // IELTS/SAT on the marketplace: present with whether the full analysis is still behind the plan.
  ...(typeof get(item, 'insights.locked') === 'boolean'
    ? {
        insights: {
          locked: get(item, 'insights.locked') as boolean,
          ...(get(item, 'insights.aiGradingPending') === true ? { aiGradingPending: true as const } : {}),
        },
      }
    : {}),
  ...(typeof get(item, 'attempt_number') === 'number' ? { attemptNumber: get(item, 'attempt_number') as number } : {}),
  ...(get(item, 'previous_attempt') && typeof get(item, 'previous_attempt.percentage') === 'number'
    ? {
        previousAttempt: {
          percentage: get(item, 'previous_attempt.percentage') as number,
          band: typeof get(item, 'previous_attempt.band') === 'number' ? (get(item, 'previous_attempt.band') as number) : null,
          total: typeof get(item, 'previous_attempt.total') === 'number' ? (get(item, 'previous_attempt.total') as number) : null,
        },
      }
    : {}),
  ...(Array.isArray(get(item, 'attempt_history'))
    ? {
        attemptHistory: (get(item, 'attempt_history') as any[])
          .filter((h) => typeof h?.percentage === 'number')
          .map((h) => ({ percentage: h.percentage, band: typeof h.band === 'number' ? h.band : null, total: typeof h.total === 'number' ? h.total : null })),
      }
    : {}),
  ...(typeof get(item, 'platform_average.percentage') === 'number' && typeof get(item, 'platform_average.attempts') === 'number'
    ? { platformAverage: { percentage: get(item, 'platform_average.percentage') as number, attempts: get(item, 'platform_average.attempts') as number } }
    : {}),
  ...(get(item, 'analysis') && typeof get(item, 'analysis') === 'object' ? { analysis: analysisOf(get(item, 'analysis')) } : {}),
  test: {
    id: get(item, 'test.id') || '',
    title: get(item, 'test.title') || '',
    description: get(item, 'test.description') || '',
    totalQuestions: get(item, 'test.total_questions') || 0,
    category: get(item, 'test.category') || undefined,
    tags: get(item, 'test.tags') || [],
    subjectConfigs: get(item, 'test.subject_configs') || get(item, 'test.subjectConfigs') || [],
  },
  user: {
    id: get(item, 'user.id') || '',
    name: get(item, 'user.name') || '',
    email: get(item, 'user.email') || '',
    avatar: get(item, 'user.avatar') || '',
  },
  answers: (get(item, 'answers') || []).map((ans: any) => ({
    questionId: get(ans, 'question_id') || '',
    questionText: get(ans, 'question_text') || '',
    questionType: get(ans, 'question_type') || '',
    sectionId: get(ans, 'section_id') || '',
    sectionTitle: get(ans, 'section_title') || '',
    answer: get(ans, 'answer') || '',
    correctAnswer: get(ans, 'correct_answer') || '',
    answerRaw: get(ans, 'answer_raw') ?? null,
    correctAnswerRaw: get(ans, 'correct_answer_raw') ?? null,
    questionData: get(ans, 'question_data') ?? null,
    explanation: get(ans, 'explanation') || get(ans, 'question_data.explanation') || '',
    aiReasoning: get(ans, 'ai_reasoning') || get(ans, 'aiReasoning') || '',
    ...(get(ans, 'insight_locked') === true ? { insightLocked: true as const } : {}),
    ...(get(ans, 'writing_feedback_locked') === true ? { writingFeedbackLocked: true as const } : {}),
    ...(get(ans, 'writing_feedback_available') === true ? { writingFeedbackAvailable: true as const } : {}),
    ...(Array.isArray(get(ans, 'writing_feedback.annotations')) && typeof get(ans, 'writing_feedback.model_answer') === 'string'
      ? {
          writingFeedback: {
            annotations: (get(ans, 'writing_feedback.annotations') as any[])
              .filter((a) => ['good', 'suggest', 'error'].includes(a?.kind) && typeof a?.start === 'number' && typeof a?.end === 'number' && typeof a?.note === 'string')
              .map((a) => ({ kind: a.kind, start: a.start, end: a.end, note: a.note })),
            modelAnswer: get(ans, 'writing_feedback.model_answer') as string,
          },
        }
      : {}),
    ...(get(ans, 'ai_analysis.available') === true
      ? {
          aiAnalysis: {
            available: true as const,
            texts: analysisTexts(get(ans, 'ai_analysis.texts')),
            ...(get(ans, 'ai_analysis.locked') === true ? { locked: true as const } : {}),
          },
        }
      : {}),
    ...(subAnalysisOffers(get(ans, 'ai_analysis_subs')) ?? {}),
    isCorrect: get(ans, 'is_correct') || false,
    // An older API sends no counts: every answer is then one item.
    itemsCorrect: get(ans, 'items_correct') ?? (get(ans, 'is_correct') ? 1 : 0),
    itemsTotal: get(ans, 'items_total') ?? 1,
    gradingPending: get(ans, 'grading_pending') === true,
    ...(Array.isArray(get(ans, 'speaking_recordings'))
      ? {
          speakingRecordings: (get(ans, 'speaking_recordings') as any[]).map((r: any) => ({
            key: r.key ?? '',
            part: r.part ?? 1,
            prompt: r.prompt ?? '',
            durationSec: r.duration_sec ?? null,
            url: r.url ?? null,
            transcript: r.transcript ?? null,
            ...(r.answered === false ? { answered: false as const } : {}),
            ...(r.transcript_locked === true ? { transcriptLocked: true as const } : {}),
            ...(r.model_answer_locked === true ? { modelAnswerLocked: true as const } : {}),
            ...(typeof r.model_answer_available === 'boolean'
              ? {
                  modelAnswerAvailable: r.model_answer_available,
                  modelAnswer: r.model_answer
                    ? {
                        mode: r.model_answer.mode === 'SAMPLE' ? ('SAMPLE' as const) : ('IMPROVED' as const),
                        currentLevel: r.model_answer.current_level ?? null,
                        targetLevel: r.model_answer.target_level,
                        text: r.model_answer.text,
                        analysis: r.model_answer.analysis,
                      }
                    : null,
                }
              : {}),
          })),
        }
      : {}),
    pointsEarned: get(ans, 'points_earned') || 0,
    maxPoints: get(ans, 'max_points') || 0,
    timeSpent: get(ans, 'time_spent') || 0,
    ...(get(ans, 'flagged') === true ? { flagged: true as const } : {}),
    options: get(ans, 'options')
      ? (get(ans, 'options') as any[]).map((o: any) => ({
          id: o.id ?? '',
          value: o.value ?? '',
          isCorrect: o.is_correct ?? false,
          isSelected: o.is_selected ?? false,
        }))
      : null,
    matchPairs: get(ans, 'match_pairs')
      ? (get(ans, 'match_pairs') as any[]).map((p: any) => ({
          key: p.key ?? '',
          correctValue: p.correct_value ?? '',
          selectedValue: p.selected_value ?? '',
        }))
      : null,
  })),
  proctoringEvents: (get(item, 'proctoring_events') || []).map((event: any) => ({
    type: get(event, 'type') || '',
    timestamp: get(event, 'timestamp') ? new Date(get(event, 'timestamp')) : new Date(),
    severity: get(event, 'severity') || 'low',
    description: get(event, 'description') || '',
  })),
});

export const Stats = (item?: any): Types.IEntity.Stats => ({
  testId: get(item, 'test_id') || '',
  totalAttempts: get(item, 'total_attempts') || 0,
  completedAttempts: get(item, 'completed_attempts') || 0,
  avgScore: get(item, 'avg_score') || 0,
  passRate: get(item, 'pass_rate') || 0,
  avgDuration: get(item, 'avg_duration') || 0,
  scoreDistribution: get(item, 'score_distribution') || [],
});

export const Meta = (item?: any): Types.IEntity.Meta => ({
  total: get(item, 'total') || 0,
  page: get(item, 'page') || 1,
  perPage: get(item, 'per_page') || 10,
  totalPages: get(item, 'total_pages') || 0,
  subjects: (get(item, 'subjects') || []).map((s: any) => ({
    id: s.id || '',
    name: s.name || '',
    maxScore: s.max_score ?? s.maxScore ?? 0,
  })),
});

export const MyStats = (item?: any): Types.IEntity.MyStats => ({
  totalAttempts: get(item, 'total_attempts') || 0,
  completedAttempts: get(item, 'completed_attempts') || 0,
  avgScore: get(item, 'avg_score') || 0,
  passRate: get(item, 'pass_rate') ?? null,
  avgDuration: get(item, 'avg_duration') || 0,
});
